import {
  FALLBACK_COLUMN_WIDTH,
  type TimelineLegendItem,
} from "./timelineUtils";

export const SUBJECT_TIMELINE_VISIBLE_COLUMNS = [
  "event_type",
  "event_name",
  "start_date",
  "end_date",
] as const;

export const SUBJECT_TIMELINE_COLUMN_LABELS: Record<string, string> = {
  event_type: "Event Type",
  event_name: "Event Name",
  start_date: "Start",
  end_date: "End",
};

const SUBJECT_TIMELINE_COLUMN_WIDTHS: Record<string, number> = {
  event_type: 170,
  event_name: 170,
  start_date: 120,
  end_date: 120,
};

const subjectLegendMapping = {
  AdverseEvent: "AE",
  ConcomitantMedication: "Conmed",
  ConcomitantProcedure: "Concomitant Procedure",
  "Data Discrepancies": "Data Discrepancies",
  EndOfStudy: "EOS",
  IndividualActivityCompletion: "Visits & Activities",
  InformedConsent: "Informed Consent",
  IpAdministration: "IP Admin",
  ProtocolDeviation: "Protocol Deviation",
  Randomization: "Randomization",
  Screening: "Screening",
} as const;

type SubjectLegendKey = keyof typeof subjectLegendMapping;

const subjectLegendBorderColor: Record<SubjectLegendKey, string> = {
  AdverseEvent: "#C275A1",
  ConcomitantMedication: "#E5945D",
  ConcomitantProcedure: "#A54605",
  "Data Discrepancies": "#004A42",
  EndOfStudy: "#3F3518",
  IndividualActivityCompletion: "#0C98D0",
  InformedConsent: "#004A42",
  IpAdministration: "#4BA06E",
  ProtocolDeviation: "#3F3518",
  Randomization: "#06709B",
  Screening: "#BCA048",
};

const subjectLegendFillColor: Record<SubjectLegendKey, string> = {
  AdverseEvent: "#EBD1E0",
  ConcomitantMedication: "#FAEEDF",
  ConcomitantProcedure: "#e5945dcc",
  "Data Discrepancies": "#66928ecc",
  EndOfStudy: "gray",
  IndividualActivityCompletion: "#D2EFF9",
  InformedConsent: "#8cdd8f",
  IpAdministration: "#BDE0CC",
  ProtocolDeviation: "#e7e8f3",
  Randomization: "#64bde1cc",
  Screening: "#fde6a0cc",
};

export function getSubjectTimelineTableColumnWidth(column: string): number {
  return SUBJECT_TIMELINE_COLUMN_WIDTHS[column] ?? FALLBACK_COLUMN_WIDTH;
}

export function getSubjectTimelineLegendFillColor(value: string): string {
  return subjectLegendFillColor[value as SubjectLegendKey] ?? "";
}

export function getSubjectTimelineLegendBorderColor(value: string): string {
  return subjectLegendBorderColor[value as SubjectLegendKey] ?? "";
}

export function getSubjectTimelineLegendBorderColorFromFill(
  fillColor: string,
): string {
  const entry = (
    Object.keys(subjectLegendFillColor) as SubjectLegendKey[]
  ).find(
    (key) =>
      subjectLegendFillColor[key].toLowerCase() === fillColor.toLowerCase(),
  );

  return entry ? subjectLegendBorderColor[entry] : "";
}

export function getSubjectTimelineLegendItems(
  keys?: string[],
): TimelineLegendItem[] {
  const legendKeys = keys?.length
    ? keys.filter((key): key is SubjectLegendKey => key in subjectLegendMapping)
    : (Object.keys(subjectLegendMapping) as SubjectLegendKey[]);

  return legendKeys
    .map((key) => ({
      key,
      label: subjectLegendMapping[key],
      borderColor: subjectLegendBorderColor[key],
      fillColor: subjectLegendFillColor[key],
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export const subjectTimelineUtils = {
  defaultVisibleColumns: SUBJECT_TIMELINE_VISIBLE_COLUMNS,
  columnLabels: SUBJECT_TIMELINE_COLUMN_LABELS,
  getTableColumnWidth: getSubjectTimelineTableColumnWidth,
  getLegendFillColor: getSubjectTimelineLegendFillColor,
  getLegendBorderColor: getSubjectTimelineLegendBorderColor,
  getLegendBorderColorFromFill: getSubjectTimelineLegendBorderColorFromFill,
  getAllLegendItems: getSubjectTimelineLegendItems,
};
